
//src/Header.tsx
import { useState } from "react";
import { useLocation } from "wouter";
import { useAuth } from "@/hooks/useAuth";
import { useCreditsBalance } from "@/hooks/useCredits";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Menu, X } from "lucide-react";

export default function Header() {
  const { isAuthenticated, user, logoutMutation } = useAuth() as any;
  const { data: creditsBalance } = useCreditsBalance();
  const credits = (typeof creditsBalance?.credits === 'number')
    ? creditsBalance.credits
    : (user?.credits ?? 0);

  const [location, navigate] = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const navItems = isAuthenticated
    ? [
        { href: "/", label: "Home" },
        { href: "/dashboard", label: "Dashboard" },
        { href: "/start-trading", label: "Start Analysis" },
        { href: "/how-it-works", label: "How It Works" },
        { href: "/pricing", label: "Pricing" },
        { href: "/profile", label: "Profile" }
      ]
    : [
        { href: "/", label: "Home" },
        { href: "/about", label: "About" },
        { href: "/how-it-works", label: "How It Works" },
        { href: "/pricing", label: "Pricing" },
        { href: "/contact", label: "Contact" }
      ];

  const handleNavClick = (href: string) => {
    setIsMobileMenuOpen(false);
    if (href.startsWith("#")) {
      const element = document.querySelector(href);
      if (element) element.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate(href);
    }
  };

  const handleLogout = () => {
    logoutMutation?.mutate(undefined, {
      onSettled: () => navigate("/login"),
    });
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-[hsl(226,26%,7%,0.95)] backdrop-blur-sm border-b border-gray-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div
            className="flex items-center space-x-2 cursor-pointer"
            onClick={() => navigate("/")}
          >
            <img src="/favicon.svg" alt="TradeSagePro logo" className="w-6 h-6" />
            <span className="text-white text-xl font-bold">TradeSagePro</span>
          </div>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-8">
            {navItems.map(item => (
              <div
                key={item.href}
                className={`transition-colors duration-200 cursor-pointer ${
                  location === item.href
                    ? "text-[hsl(193,100%,50%)]"
                    : "text-[hsl(210,9%,91%)] hover:text-[hsl(193,100%,50%)]"
                }`}
                onClick={() => handleNavClick(item.href)}
              >
                {item.label}
              </div>
            ))}
          </div>

          {/* Desktop Right Section */}
          <div className="hidden md:flex items-center space-x-4">
            {isAuthenticated ? (
              <div className="flex items-center space-x-4">
                <div className="text-[hsl(210,9%,91%)] text-sm">
                  Tokens:{" "}
                  <span className="text-[hsl(193,100%,50%)] font-semibold">
                    {credits}
                  </span>
                </div>
                <Button
                  variant="ghost"
                  className="text-[hsl(210,9%,91%)] hover:text-white"
                  onClick={handleLogout}
                  disabled={logoutMutation?.isPending}
                >
                  {logoutMutation?.isPending ? 'Signing Out…' : 'Sign Out'}
                </Button>
                <Button
                  className="bg-[hsl(193,100%,50%)] text-[hsl(226,26%,7%)] hover:scale-105 transition-all duration-300"
                  onClick={() => navigate("/start-trading")}
                >
                  Start Analysis
                </Button>
              </div>
            ) : (
              <>
                <Button
                  variant="ghost"
                  className="text-[hsl(210,9%,91%)] hover:text-white"
                  onClick={() => navigate("/login")}
                >
                  Sign In
                </Button>
                <Button
                  className="bg-[hsl(193,100%,50%)] text-[hsl(226,26%,7%)] hover:scale-105 transition-all duration-300"
                  onClick={() => navigate("/login")}
                >
                  Start Analysis
                </Button>
              </>
            )}
          </div>

          {/* Mobile Group */}
          <div className="flex md:hidden items-center gap-2">
            <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" className="md:hidden text-[hsl(193,100%,50%)]">
                  <Menu className="w-6 h-6" />
                </Button>
              </SheetTrigger>
              <SheetContent
                side="right"
                className="bg-[hsl(226,26%,7%)] border-gray-700"
              >
                <div className="flex items-center justify-between mb-8">
                  <div className="flex items-center space-x-2">
                    <img src="/favicon.svg" alt="TradeSagePro logo" className="w-6 h-6" />
                    <span className="text-white text-lg font-bold">TradeSagePro</span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="text-[hsl(210,9%,91%)]"
                  >
                    <X className="w-5 h-5" />
                  </Button>
                </div>

                <div className="space-y-4">
                  {navItems.map(item => (
                    <div
                      key={item.href}
                      className={`block py-2 transition-colors duration-200 cursor-pointer ${
                        location === item.href
                          ? "text-[hsl(193,100%,50%)]"
                          : "text-[hsl(210,9%,91%)] hover:text-[hsl(193,100%,50%)]"
                      }`}
                      onClick={() => handleNavClick(item.href)}
                    >
                      {item.label}
                    </div>
                  ))}

                  <div className="pt-4 border-t border-gray-700 space-y-2">
                    {isAuthenticated ? (
                      <>
                        <div className="py-2 text-[hsl(210,9%,91%)] text-sm">
                          Tokens:{" "}
                          <span className="text-[hsl(193,100%,50%)] font-semibold">
                            {credits}
                          </span>
                        </div>
                        <Button
                          variant="ghost"
                          className="w-full justify-start text-[hsl(210,9%,91%)] hover:text-white"
                          onClick={() => {
                            handleLogout();
                            setIsMobileMenuOpen(false);
                          }}
                          disabled={logoutMutation?.isPending}
                        >
                          {logoutMutation?.isPending ? 'Signing Out…' : 'Sign Out'}
                        </Button>
                        <Button
                          className="w-full bg-[hsl(193,100%,50%)] text-[hsl(226,26%,7%)]"
                          onClick={() => {
                            navigate("/start-trading");
                            setIsMobileMenuOpen(false);
                          }}
                        >
                          Start Analysis
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          variant="ghost"
                          className="w-full justify-start text-[hsl(210,9%,91%)] hover:text-white"
                          onClick={() => {
                            navigate("/login");
                            setIsMobileMenuOpen(false);
                          }}
                        >
                          Sign In
                        </Button>
                        <Button
                          className="w-full bg-[hsl(193,100%,50%)] text-[hsl(226,26%,7%)]"
                          onClick={() => {
                            navigate("/login");
                            setIsMobileMenuOpen(false);
                          }}
                        >
                          Start Analysis
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </nav>
  );
}

// Notifications removed as per request.